import Div from '@hellodarwin/core/lib/components/common/div';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import { OptionalRecord } from '@hellodarwin/core/lib/features/entities';
import useMediaQuery from '@hellodarwin/core/lib/features/helpers/use-media-query';
import { default as Progress } from 'antd/es/progress';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { StepContent } from '../../../../../../data/steps-content';
import { useOnboardingFlow } from '../../../../../../hooks/useOnboardingFlow';
import { OnboardingStep } from '../../../../../../types/onboarding';
import kebabToCamelCase from '../../../../../../utils/kebabToCamelCase';

/**
 * Stepper component to show the user's progress through the onboarding flow
 */
interface OnboardingStepperStep {
  key: OnboardingStep;
  title: string;
}

const formatSteps = (
  stepsContent: OptionalRecord<OnboardingStep, StepContent>,
  t: (key: string) => string,
): Array<OnboardingStepperStep> => {
  return (Object.keys(stepsContent) as OnboardingStep[])
    .filter((step) => !stepsContent[step]?.hideFromFlow)
    .map((step) => ({
      key: step,
      title: t(`onboardingClient|${kebabToCamelCase(step, 'onboarding')}`),
    }));
};

const OnboardingStepper: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { currentStep, stepsContent } = useOnboardingFlow();

  const isDesktop = useMediaQuery({ minWidth: theme.breakpoints.l });

  // Define steps based on the user's path (grants or RFP)
  const steps = useMemo((): Array<OnboardingStepperStep> => {
    return formatSteps(stepsContent, t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsContent]);

  // Calculate current step index
  const currentStepIndex = steps.findIndex((step) => step.key === currentStep);

  const progressPercent = useMemo(() => {
    return Math.round(((currentStepIndex + 1) / (steps.length + 1)) * 100);
  }, [currentStepIndex, steps.length]);

  return (
    <Div
      flex='row'
      gap={theme.spacing.md}
      style={{
        width: '100%',
        height: 'fit-content',
        paddingBlock: isDesktop ? theme.spacing.md : theme.spacing.sm,
        paddingInline: theme.spacing.md,
        justifyContent: 'space-between', 
        alignItems: 'center'
      }}
    >
      <Progress
        percent={progressPercent}
        showInfo={false}
        strokeColor={theme.colors.green_high_contrast}
        trailColor={theme.colors.grey_5}
        size={{ height: 14 }}
      />
      <Typography elementTheme="subtitle2">{progressPercent}%</Typography>
    </Div>
  );
};

export default OnboardingStepper;
